import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faEye,
  faFlagCheckered,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  finalizeProviderMigration,
  previewFinalizeProviderMigration,
  type ProviderMigrationFinalizeResultDto,
} from '../api/provider-migrations.api';
import { parseMigrationError, type ParsedMigrationError } from '../migration-errors';
import { MigrationErrorNotice } from './MigrationErrorNotice';

/**
 * Finalize a verified provider migration — VERIFYING -> COMPLETED.
 *
 * ── WHEN THIS PANEL MAY BE RENDERED ────────────────────────────────────────
 * ONLY at workflow stage `FINALIZE_READY`, which requires status `VERIFYING`
 * AND every item verified AND the server's own confirmation that every asset is
 * already owned by the target provider. The page owns that decision; see
 * `deriveMigrationWorkflow`.
 *
 * It used to be rendered on `status === 'VERIFYING'` alone, which is also the
 * state a migration sits in BEFORE its ownership transition. Finalization gate
 * `ASSET_NOT_ON_TARGET_PROVIDER` refuses in that state, so the panel offered, as
 * the next action, the one action the server was guaranteed to refuse. The gate
 * was right; the panel's visibility condition was wrong. The gate is untouched.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE ───────────────────────────────
 * `finalizeProviderMigration()` is called from EXACTLY ONE place: the confirm
 * handler of the dialog below, which cannot run until a human has opened the
 * dialog and pressed the confirm button. There is deliberately:
 *
 *   • no `useEffect` in this file — nothing fires on mount, render or update;
 *   • no polling, interval, timeout, retry or background task;
 *   • no auto-submit, and no call on the panel becoming visible;
 *   • no path from loading or refreshing the page to a POST.
 *
 * Those absences are the enforcement. A reviewer can confirm it by grepping
 * this file for `finalizeProviderMigration` — there is one call site.
 *
 * The gate rehearsal (`previewFinalizeProviderMigration`) is a POST that writes
 * nothing, is also reachable only from a click, and is NOT a prerequisite: the
 * real finalization re-runs all twenty gates server-side regardless, so a stale
 * rehearsal cannot license a transition the server would now refuse.
 *
 * ── WHY THE CONFIRMATION IS BLUNT ──────────────────────────────────────────
 * `COMPLETED` is terminal, and `/ownership/rollback` requires status
 * `VERIFYING`. Finalizing therefore permanently closes the ownership rollback
 * endpoint for this migration. The source objects are still retained on the
 * source provider — nothing is deleted by finalization, now or ever — but the
 * one-call reversal path is gone afterwards. The dialog says so plainly rather
 * than burying it.
 */

export interface FinalizeMigrationPanelProps {
  migrationId: string;
  /** Objects verified by the copy phase, for the operator's own sanity check. */
  verifiedObjects: number;
  totalObjects: number | null;
  /**
   * Display name of the provider whose copies stay retained. A PROP, not a
   * constant: this sentence used to hardcode one provider's name and was
   * therefore literally inverted for any migration in the other direction.
   */
  sourceName: string;
  /** Display name of the provider that now owns the assets. */
  targetName: string;
  /**
   * Called with the server's result after a successful finalization.
   *
   * The page owns the outcome, not this panel: reloading the migration
   * unmounts the panel (status is no longer VERIFYING), so a success message
   * held here would be destroyed the moment it was earned.
   */
  onFinalized: (result: ProviderMigrationFinalizeResultDto) => void;
}

export function FinalizeMigrationPanel({
  migrationId,
  verifiedObjects,
  totalObjects,
  sourceName,
  targetName,
  onFinalized,
}: FinalizeMigrationPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ProviderMigrationFinalizeResultDto | null>(null);
  const [error, setError] = useState<ParsedMigrationError | null>(null);

  const busy = submitting || previewing;

  /** The ONLY call site for the gate rehearsal. Writes nothing. */
  const runPreview = async () => {
    if (busy) return; // no duplicate submissions
    setPreviewing(true);
    setError(null);
    try {
      setPreview(await previewFinalizeProviderMigration(migrationId));
    } catch (err) {
      setPreview(null);
      setError(parseMigrationError(err));
    } finally {
      setPreviewing(false);
    }
  };

  /**
   * The ONLY call site for finalization. Reachable solely from the confirm
   * button inside the dialog, which is itself reachable solely from an
   * explicit click.
   */
  const confirmFinalize = async () => {
    if (busy) return; // no duplicate submissions
    setSubmitting(true);
    setError(null);
    try {
      const r = await finalizeProviderMigration(migrationId);
      setConfirming(false);
      onFinalized(r);
    } catch (err) {
      // The server's refusal is authoritative and is shown in full — gate name
      // included. No retry loop: a refused finalization is a decision for the
      // operator.
      setError(parseMigrationError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="finalize-panel">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <FontAwesomeIcon icon={faFlagCheckered} className="h-4 w-4 text-muted-foreground" />
        Finalize migration
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        This migration is <strong className="text-foreground">verified</strong> and its objects are{' '}
        <strong className="uppercase text-foreground">owned by {targetName}</strong>. Finalizing
        records it as <strong className="text-foreground">COMPLETED</strong>. It copies nothing,
        moves nothing and <strong className="text-foreground">deletes nothing</strong>.
      </p>

      {error && <MigrationErrorNotice error={error} testId="finalize-error" />}

      {preview && (
        <div
          className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3"
          data-testid="finalize-preview-result"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-sky-700">
            <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
            Rehearsal complete — nothing was changed
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {preview.gatesPassed} of {preview.gatesEvaluated} completion gates pass ·{' '}
            {preview.targetObjectsRehashed} destination objects re-hashed ·{' '}
            {preview.sourceObjectsConfirmedPresent} source objects confirmed still present ·{' '}
            {preview.migrationRowsWritten} rows written.
          </p>
        </div>
      )}

      {!confirming && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="finalize-preview"
            disabled={busy}
            onClick={() => void runPreview()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {previewing ? (
              <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />
            ) : (
              <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
            )}
            {previewing ? 'Rehearsing…' : 'Preview Finalization'}
          </button>

          <button
            type="button"
            data-testid="finalize-open"
            disabled={busy}
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faFlagCheckered} className="h-3.5 w-3.5" />
            Finalize Migration
          </button>
        </div>
      )}

      {confirming && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="finalize-confirm-title"
          className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4"
          data-testid="finalize-confirm"
        >
          <p
            id="finalize-confirm-title"
            className="flex items-center gap-2 text-sm font-semibold text-amber-700"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
            Confirm finalization — this cannot be undone through the migration API
          </p>

          <ul
            className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-foreground"
            data-testid="finalize-confirm-points"
          >
            <li>
              This migration is <strong>already verified</strong> — {verifiedObjects}
              {totalObjects === null ? '' : ` of ${totalObjects}`} objects were copied and
              checksum-verified.
            </li>
            <li>
              Ownership is <strong>already on {targetName.toUpperCase()}</strong>. Finalization does
              not move anything.
            </li>
            <li>
              Finalization changes the migration from <strong>VERIFYING</strong> to{' '}
              <strong>COMPLETED</strong>.
            </li>
            <li>
              Finalization <strong>permanently closes the ownership rollback path</strong> for this
              migration.
            </li>
            <li>
              The <strong className="uppercase">{sourceName}</strong>{' '}
              <strong>source objects are currently retained</strong> and finalization does not
              delete them.
            </li>
            <li>
              <strong>
                This action cannot be undone through the migration rollback endpoint after
                completion.
              </strong>
            </li>
          </ul>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              data-testid="finalize-confirm-submit"
              disabled={submitting}
              onClick={() => void confirmFinalize()}
              className="inline-flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />
              ) : (
                <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5" />
              )}
              {submitting ? 'Finalizing…' : 'Yes, finalize this migration'}
            </button>
            <button
              type="button"
              data-testid="finalize-confirm-cancel"
              disabled={submitting}
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
